import React from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { FormProvider, useForm, useFormContext } from "react-hook-form";

import { formControlCssClass } from "../../utils/errors";

import { ProductProps } from "./cart-types";
import { setContact, useCartStateDispatch } from "./cartSlice";
import {
  useCartCustomerContactMutation,
  type CartNew
} from "./piosolverStoreApi";
import type { PriceCalcResult } from "./piosolverStoreApi";
import useStatusNavigate from "./useStatusNavigate";

const SUPPORT_EMAIL_ADDRESS = "support@piosolver.com";

type PriceCalculationErrorProps = { data?: PriceCalcResult };
function PriceCalculationError({ data }: PriceCalculationErrorProps) {
  if (data == null) return null;
  if (data.error == null) return null;
  if (data.error === "BadRequest" || data.error === "Unknown") {
    return (
      <div>
        There was a problem calculating the price for the items in your cart.
        Please to back and try again. If the problem persists, please contact us
        at <b>{SUPPORT_EMAIL_ADDRESS}</b>.
      </div>
    );
  }
  if (data.error === "ContactUs") {
    return (
      <div>
        Please reach out to us at <b>{SUPPORT_EMAIL_ADDRESS}</b> to discuss
        upgrading your license.
      </div>
    );
  }
  return <div>{data.message}</div>;
}

type EmailInformationProps = {
  disabled?: boolean;
};
function EmailInformation({ disabled }: EmailInformationProps) {
  const {
    register,
    formState: { errors }
  } = useFormContext();
  return (
    <Form.Group className="mb-3">
      <Form.Label>Contact email</Form.Label>
      <input
        className={formControlCssClass(errors.contactEmail != null)}
        id="contactEmail"
        type="email"
        placeholder="name@example.com"
        autoComplete="email"
        disabled={disabled}
        {...register("contactEmail", {
          required: true,
          // From https://www.emailregex.com
          pattern:
            // eslint-disable-next-line max-len
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        })}
      />
      {errors.contactEmail ? (
        <span className="invalid-feedback">
          Please provide an email address.
        </span>
      ) : (
        <Form.Text className="text-muted">
          The contact email is associated with the license key.
        </Form.Text>
      )}
    </Form.Group>
  );
}

function NameInformation() {
  const {
    register,
    formState: { errors }
  } = useFormContext();
  return (
    <div className="mb-3">
      <div className="d-flex mb-3">
        <div className="me-1">
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <input
              className={formControlCssClass(errors.givenName != null)}
              id="givenName"
              placeholder="First Name"
              type="text"
              autoComplete="given-name"
              {...register("givenName", {
                required: false
              })}
            />
            {errors.givenName && (
              <span className="invalid-feedback">
                Please provide a first name.
              </span>
            )}
          </Form.Group>
        </div>
        <div className="flex-grow-1">
          <Form.Group>
            <Form.Label>&nbsp;</Form.Label>
            <input
              className={formControlCssClass(errors.familyName != null)}
              id="familyName"
              placeholder="Last Name"
              type="text"
              autoComplete="family-name"
              {...register("familyName", {
                required: true
              })}
            />
            {errors.familyName && (
              <span className="invalid-feedback">
                Please provide a last name.
              </span>
            )}
          </Form.Group>
        </div>
      </div>
      <div>
        <input
          className={formControlCssClass(errors.companyName != null)}
          id="companyName"
          placeholder="Company"
          type="text"
          autoComplete="organization"
          {...register("companyName", {
            required: false
          })}
        />
      </div>
    </div>
  );
}

type ContactProps = Pick<ProductProps, "state"> & { cart: CartNew };

function Contact({ cart, state }: ContactProps) {
  const cartId = cart.cartId;
  const [updateCustomer, { error: _error, isLoading: _isLoading }] =
    useCartCustomerContactMutation();
  const dispatch = useCartStateDispatch();
  const statusNavigate = useStatusNavigate();
  const initialContactEmail =
    state.contactEmail ?? cart.customerContact?.contactEmail;
  const formMethods = useForm({
    defaultValues: {
      contactEmail: initialContactEmail,
      givenName: state.contactName?.givenName,
      familyName: state.contactName?.familyName,
      companyName: state.contactName?.companyName
    }
  });
  const calculatedPrice = state.calculatedPrice;
  if (calculatedPrice?.error != null) {
    return (
      <>
        <PriceCalculationError data={state.calculatedPrice} />
        <div className="d-flex">
          <Button variant="link" onClick={() => statusNavigate("initial")}>
            Back
          </Button>
        </div>
      </>
    );
  }
  /* eslint-disable @typescript-eslint/no-non-null-assertion */
  return (
    <FormProvider {...formMethods}>
      <form
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={formMethods.handleSubmit(async (data) => {
          const contact = {
            contactEmail: data.contactEmail,
            contactName: {
              givenName: data.givenName,
              familyName: data.familyName!,
              companyName: data.companyName
            }
          };
          try {
            await updateCustomer({ cartId, ...contact });
          } finally {
            // it is not a big problem if the update fails, we can still continue
            dispatch(setContact(contact));
          }
        })}>
        <EmailInformation
          disabled={cart.customerContact?.contactEmail != null}
        />
        <div className="mb-1">&nbsp;</div>
        <NameInformation />
        <div className="d-flex flex-row-reverse justify-content-between">
          <Button variant="primary" type="submit">
            Continue to Payment
          </Button>
          <Button variant="link" onClick={() => statusNavigate("initial")}>
            Back
          </Button>
        </div>
      </form>
    </FormProvider>
  );
  /* eslint-enable @typescript-eslint/no-non-null-assertion */
}

export default Contact;
